import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useUser } from '@/hooks/auth/use-user';
import { userQueryKeys } from '@/lib/query-keys';

/**
 * Enhanced hook for comprehensive AI usage cache invalidation
 * Provides both immediate (hard) and deferred (soft) invalidation strategies
 * Automatically handles all usage-related cache keys for real-time updates
 */
export const useAIUsageCacheInvalidation = () => {
  const queryClient = useQueryClient();
  const { user } = useUser();

  /**
   * Hard invalidation - Immediate refetch for real-time updates
   * Use after AI operations where user expects immediate feedback
   */
  const invalidateAIUsageDataHard = useCallback(async () => {
    if (!user?.id) return;

    try {
      // Invalidate all AI usage related queries immediately
      await Promise.all([
        // Primary usage data (navbar + billing page)
        queryClient.invalidateQueries({
          queryKey: userQueryKeys.aiUsageData(user.id),
        }),
        
        // Usage analytics (billing page)
        queryClient.invalidateQueries({
          queryKey: userQueryKeys.usageOverview(user.id),
          exact: false, // All time ranges
        }),
        
        queryClient.invalidateQueries({
          queryKey: userQueryKeys.usageAnalytics(user.id),
          exact: false, // All time ranges
        }),
        
        // Cost summaries
        queryClient.invalidateQueries({
          queryKey: userQueryKeys.costSummary(user.id),
          exact: false, // All time ranges
        }),
      ]);

      console.log('🔄 AI usage cache invalidated (HARD) - immediate refetch triggered');
    } catch (error) {
      console.error('❌ Hard cache invalidation failed:', error);
      // Fallback to soft invalidation
      invalidateAIUsageDataSoft();
    }
  }, [queryClient, user?.id]);

  /**
   * Soft invalidation - Mark as stale for next access
   * Use for background operations or when immediate update isn't critical
   */
  const invalidateAIUsageDataSoft = useCallback(() => {
    if (!user?.id) return;

    try {
      // Mark all usage queries as stale (will refetch on next access)
      queryClient.invalidateQueries({
        queryKey: userQueryKeys.aiUsageData(user.id),
        refetchType: 'none', // Don't refetch immediately
      });

      queryClient.invalidateQueries({
        queryKey: userQueryKeys.usageOverview(user.id),
        exact: false,
        refetchType: 'none',
      });

      queryClient.invalidateQueries({
        queryKey: userQueryKeys.usageAnalytics(user.id),
        exact: false,
        refetchType: 'none',
      });

      queryClient.invalidateQueries({
        queryKey: userQueryKeys.costSummary(user.id),
        exact: false,
        refetchType: 'none',
      });

      console.log('🔄 AI usage cache invalidated (SOFT) - marked stale for next access');
    } catch (error) {
      console.error('❌ Soft cache invalidation failed:', error);
    }
  }, [queryClient, user?.id]);

  /**
   * Smart invalidation - Automatically chooses hard or soft based on context
   * @param immediate - Whether user expects immediate visual feedback
   */
  const invalidateAIUsageData = useCallback(async (immediate: boolean = true) => {
    if (immediate) {
      await invalidateAIUsageDataHard();
    } else {
      invalidateAIUsageDataSoft();
    }
  }, [invalidateAIUsageDataHard, invalidateAIUsageDataSoft]);

  return {
    invalidateAIUsageData,
    invalidateAIUsageDataHard,
    invalidateAIUsageDataSoft,
  };
}; 